import { Component, computed, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { filter, map } from 'rxjs/operators';
import { NavigationStart, NavigationEnd, NavigationCancel, NavigationError, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { User as ddUser } from '@datadog/browser-core';
import { datadogLogs } from '@datadog/browser-logs';
import { redactUrl, sanitizeLogEvent, toDatadogUser } from '../datadog-redaction';
import { faArrowRight, faBell, faExternalLink, faHouse, faMessages, faUser, faExpand } from '@fortawesome/pro-solid-svg-icons';
import { NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';

import { BusinessAreaEnum } from './core/enums/generated/BusinessAreaEnum';
import { NotificationType } from './core/enums/generated/NotificationType';
import { PermissionRole } from './core/enums/generated/PermissionRole';
import { TrainingStatusEnum } from './core/enums/generated/TrainingStatusEnum';
import { InactivityTimeoutService } from './core/inactivity-timeout/inactivity-timeout.service';
import { SpinnerService } from './core/layout/spinner/spinner.service';
import { NotificationsService } from './core/pages/dashboard/widgets/notifications.service';
import { SecurityService } from './core/security/security.service';
import { ChatroomService } from './shared/chatroom/chatroom.service';
import { UserJsVm } from './shared/generated/Administration/Models/Users/UserJsVm';
import { pcgSettings } from './shared/generated/pcg-settings';
import { DATADOG_CLIENT_TOKEN } from '../datadog_config';
import { NavigationService } from './shared/navigation/navigation.service';
import { GlobalService } from './shared/services/global.service';
import { IssuanceTourService } from './formulary-inventory/issuance-tour/issuance-tour.service';

/**
 * This is the application's root component.
 */
@Component({
	selector: 'pcg-root'
    , templateUrl: './app.component.html'
    , styleUrls: ['./app.component.scss']
    , standalone: false
    , changeDetection: ChangeDetectionStrategy.Eager
    , host: {
		'(window:resize)': 'onResize()',
		'(window:beforeprint)': 'closeNav()',
		'(window:afterprint)': 'resetNav()'
	}
})
export class AppComponent implements OnInit, OnDestroy {

	@ViewChild('tabPanel') tabPanel: any;

    fileLogoUrl = "../assets/images/logos/logo_small.png";
	
	errorCache: any = {};
	
	// Keeps track if nav was fixed or not before closing for print
	navState: boolean = false;
	navService = inject(NavigationService);
    notificationsService = inject(NotificationsService);
    spinnerService = inject(SpinnerService);
    chatroomService = inject(ChatroomService);
    swu = inject(SwUpdate);

    /**
     * True on the signed-in Insulin module's pages, which paint themselves a light grey and want the
     * shell around them - the gutter and the footer strip - to match rather than frame them in white.
     *
     * The value is the top-level nav item's name, which is what selectedMainNav holds.
     */
    isInsulinArea(): boolean {
        return this.navService?.selectedMainNav() === BusinessAreaEnum.toDisplay(BusinessAreaEnum.InsulinProgram);
    }

    onResize() { 
		if (this.navService.isMobile()) { 
			localStorage.setItem("isNavFixed", "false"); 
			this.navService.toggleNavFixed(false); 
			this.navService.toggleNavOpen(false);
		}
	}

	// Ensure nav is not fixed open before printing
	closeNav() {
		this.navState = this.navService.isNavFixed();
		this.navService.toggleNavFixed(false);
	}

	resetNav() { this.navService.toggleNavFixed(this.navState); }

	// Nav
	isLoggedOn = signal<boolean>(false);
    isLoading = signal<boolean>(false);
	mode = pcgSettings.mode; //for responsive BETA text

    // #region Training-mode banner (dev/demo only)
    /** Current user's training status, tracked reactively so the banner shows/hides without a reload. */
    private trainingUser = signal<UserJsVm | null>(null);
    /** Show the training banner only to users still in training, and only outside Production. */
    showTrainingBanner = computed(() => {
        const u = this.trainingUser();
        return !!u && u.trainingStatus === TrainingStatusEnum.InTraining && this.mode !== 'Production';
    });
    /** After completing training, flip status locally to TrainingComplete so the banner hides at once. */
    onTrainingCompleted() {
        this.trainingUser.update((u) => (u ? { ...u, trainingStatus: TrainingStatusEnum.TrainingComplete } : u));
    }
    // #endregion

	// These should be managed in the navigation routes, use only for special cases
	faIconName = { faUser, faArrowRight, faBell, faMessages, faExternalLink, faExpand, faHouse };

	// My Profile
	user!: UserJsVm;

	// Enums
    busArea = BusinessAreaEnum;
	role = PermissionRole;
    notificationType = NotificationType;

	// Inactivity timeout needs to be injected here to have a constant timeout going in the background
	constructor(
		elRef: ElementRef
        , public gs: GlobalService
		, private readonly inactivityService: InactivityTimeoutService
		, public sec: SecurityService
		, private readonly modalConfig: NgbModalConfig
        , private readonly router: Router
        , private readonly issuanceTour: IssuanceTourService
	) {
        this.updateCheck();
		this.configureDatadog();
		this.configureModal(); // Default modal settings, such as disabling the animation
		sec.user$.subscribe(user => {
			// For 'My Profile' nav functionality
			this.user = user;
			this.trainingUser.set(user ?? null); // drives the training-mode banner
			const isLoggedIn: boolean = sec.isLoggedOn();
			this.isLoggedOn.set(isLoggedIn);
			if (isLoggedIn) { elRef.nativeElement.classList.add('is-logged-in'); }
			else { elRef.nativeElement.classList.remove('is-logged-in'); }
			// Offer the issuance walkthrough on login from anywhere (usually the dashboard). Idempotent and
			// self-gating (non-prod training users who haven't finished); guides them into an inventory to start.
			if (isLoggedIn) { this.issuanceTour.begin(); }
		});
		window['$router'] = this.router;
	}

	ngOnInit() {  
        // the below code is supposed to display a spinner when you click a nav item so
        // users know the click actually worked instead of getting no feedback on the click event
        // For some reason it messed with the SSE stuff so commenting out for now
        /* this.router.events.pipe(
            filter((e) =>
                e instanceof NavigationStart 
                || e instanceof NavigationEnd 
                || e instanceof NavigationCancel 
                || e instanceof NavigationError
            )
            , map((e) => e instanceof NavigationStart)
        ).subscribe(e => { 
            if (!e) {
                if ("serviceWorker" in navigator) {
                    navigator.serviceWorker.register(
                        'ngsw-worker.js'
                        , { updateViaCache : 'none' }
                    ).then((reg) => { reg.update(); }) // update the service worker on every page change
                }
            }
            this.isLoading.set(e); 
        }); */
		// Remember if nav is fixed
		if (this.navService.isMobile()) { this.navService.toggleNavFixed(false); } 
		else { this.navService.toggleNavFixed(localStorage.getItem("isNavFixed") === true.toString()); }         
	}

    updateCheck(): void {
		this.swu.checkForUpdate();
		/* this.swUpdate.checkForUpdate()
			.then(() => this.updateCheckText = 'resolved')
			.catch(err => this.updateCheckText = `rejected: ${err.message}`); */
	}

	/** Set default ng-bootstrap modal options */
	configureModal() {
		// Disable ng bootstrap modal animations by default
		this.modalConfig.animation = false;
		this.modalConfig.ariaLabelledBy = 'modal-basic-title';
	}
	
	configureDatadog() {
		if (pcgSettings.mode.toLowerCase() !== 'development') {
			datadogLogs.setGlobalContext({ env: pcgSettings.mode });
			datadogLogs.setGlobalContextProperty('referrer', redactUrl(document.referrer));
			datadogLogs.logger.setLevel('warn');
	
			this.initDataDog();
			const user = this.getDatadogUser(this.sec.getUser());
			datadogLogs.setGlobalContext({ user });
			
			this.sec.user$.subscribe(newUser => { 
				this.initDataDog();
				const updatedUser = this.getDatadogUser(newUser);
				datadogLogs.setGlobalContext({ user: updatedUser });
			});
		}
	}

	initDataDog() {
        try {
            datadogLogs.clearUser();
            const initConfig = datadogLogs.getInitConfiguration();
            if (
                initConfig === null
                || initConfig === undefined
            ) { // stop datadog init if it is already initialized
                datadogLogs.init({
                    clientToken: DATADOG_CLIENT_TOKEN
                    , trackingConsent: 'granted'
                    , site: "us5.datadoghq.com"
                    , service: "Homer" + pcgSettings?.mode
                    , version: "d128203fed"
                    , env: "Homer" + pcgSettings?.mode
                    , forwardErrorsToLogs: true
                    // Strips query strings and credential-shaped text from every log before it leaves the browser.
                    , beforeSend: sanitizeLogEvent
                    , sessionSampleRate: 100			
                });
            }
        } catch (ex) { console.error('Failed to initialize datadog:', ex); }
	}

	// Datadog receives the user's id and name, so logs can be matched to who was using the app.
	getDatadogUser(userJs: UserJsVm): ddUser {
		let ddUser : ddUser = toDatadogUser(userJs) ?? {};
		return ddUser;
	}

    backdropClick() {
        this.navService.isNotificationsNavOpen.set(false); 
        this.navService.closeAllNavs();
        this.chatroomService.resetChatroomSignals();
    }

    userUrl = () => `/admin/users/user/edit/details/${this.user?.id}`;
	ngOnDestroy(): void { this.inactivityService.stopTracking(); }
}
